import React, { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import LogoUploader from "../components/LogoUploader";
import CoverImageUploader from "../components/CoverImageUploader";
import { NotificationToggle } from "../components/PWAInstallBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { 
  Save, 
  QrCode,
  Copy,
  ExternalLink,
  Clock,
  Palette,
  Bell,
  Image,
  Instagram,
  Facebook,
  Globe,
  MessageCircle,
  CalendarX,
  Plus,
  Trash2,
  Plane,
  CreditCard,
  Euro,
  Link,
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageCircle as WhatsAppIcon,
  Building2,
  MapPin,
  Store,
  Edit3,
  ChevronDown,
  ChevronUp,
  Briefcase,
  X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, isWithinInterval, addDays } from "date-fns";
import { it, enUS } from "date-fns/locale";

// WhatsApp Reminder Status Component (Read-only for Salon Admin)
const WhatsAppReminderStatus = ({ language }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/reminder-status`, {
        withCredentials: true
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching WhatsApp status:", error);
      setStatus({ configured: false, enabled: false });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {language === "it" ? "Caricamento..." : "Loading..."}
      </div>
    );
  }

  if (status?.configured && status?.enabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
          <CheckCircle className="w-6 h-6 text-emerald-500" />
          <div className="flex-1">
            <p className="font-medium text-emerald-700 dark:text-emerald-300">
              {language === "it" ? "Reminder WhatsApp Attivi" : "WhatsApp Reminders Active"}
            </p>
            {status.phone_number && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                {status.phone_number}
              </p>
            )}
          </div>
        </div>
        
        {/* Show what reminders are enabled */}
        <div className="p-4 rounded-lg bg-muted/30 space-y-2">
          <p className="font-medium text-sm mb-3">{language === "it" ? "Notifiche automatiche:" : "Automatic notifications:"}</p>
          
          {status.settings?.send_booking_confirmation && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              {language === "it" ? "Conferma prenotazione" : "Booking confirmation"}
            </div>
          )}
          
          {status.settings?.send_reminder_12h && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              {language === "it" ? "Reminder 12 ore prima" : "Reminder 12h before"}
            </div>
          )}
          
          {status.settings?.send_reminder_2h && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              {language === "it" ? "Reminder 2 ore prima" : "Reminder 2h before"}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {language === "it" 
            ? "I reminder vengono inviati automaticamente ai clienti che prenotano con numero di telefono."
            : "Reminders are automatically sent to clients who book with a phone number."
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
        <AlertCircle className="w-6 h-6 text-slate-400" />
        <div className="flex-1">
          <p className="font-medium text-slate-600 dark:text-slate-300">
            {language === "it" ? "Reminder WhatsApp Non Attivi" : "WhatsApp Reminders Not Active"}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {language === "it" 
              ? "Contatta SmartBooker per attivare i reminder automatici per i tuoi clienti."
              : "Contact SmartBooker to enable automatic reminders for your clients."
            }
          </p>
        </div>
      </div>
      
      <div className="text-sm text-muted-foreground space-y-1">
        <p>✨ {language === "it" ? "Con i reminder WhatsApp i tuoi clienti riceveranno:" : "With WhatsApp reminders your clients will receive:"}</p>
        <ul className="list-disc list-inside ml-2 space-y-1">
          <li>{language === "it" ? "Conferma immediata della prenotazione" : "Instant booking confirmation"}</li>
          <li>{language === "it" ? "Promemoria 12 ore e 2 ore prima dell'appuntamento" : "Reminders 12 hours and 2 hours before appointment"}</li>
        </ul>
      </div>

      <a 
        href="mailto:info@smartbooker.it?subject=Attivazione%20Reminder%20WhatsApp" 
        className="inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium"
      >
        <Bell className="w-4 h-4" />
        {language === "it" ? "Richiedi attivazione" : "Request activation"}
      </a>
    </div>
  );
};

// Stripe Connect Component
const StripeConnectSection = ({ language }) => {
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    fetchConnectStatus();
    
    // Check URL params for Stripe redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_connected") === "true") {
      toast.success(language === "it" ? "Account Stripe collegato!" : "Stripe account connected!");
      fetchConnectStatus();
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("stripe_refresh") === "true") {
      toast.info(language === "it" ? "Completa la configurazione Stripe" : "Complete Stripe setup");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const fetchConnectStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/connect/status`, {
        withCredentials: true
      });
      setConnectStatus(response.data);
    } catch (error) {
      console.error("Error fetching Stripe Connect status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.post(`${API}/payments/connect/onboard`, {}, {
        withCredentials: true,
        headers: { 'Origin': window.location.origin }
      });
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore connessione Stripe");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(language === "it" 
      ? "Sei sicuro di voler disconnettere il tuo account Stripe? I pagamenti andranno alla piattaforma."
      : "Are you sure you want to disconnect your Stripe account? Payments will go to the platform."
    )) return;

    setDisconnecting(true);
    try {
      await axios.post(`${API}/payments/connect/disconnect`, {}, {
        withCredentials: true
      });
      setConnectStatus({ connected: false });
      toast.success(language === "it" ? "Account Stripe disconnesso" : "Stripe account disconnected");
    } catch (error) {
      toast.error("Errore");
    } finally {
      setDisconnecting(false);
    }
  };

  const openDashboard = async () => {
    try {
      const response = await axios.get(`${API}/payments/connect/dashboard-link`, {
        withCredentials: true
      });
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, "_blank");
      }
    } catch (error) {
      toast.error("Errore apertura dashboard Stripe");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {language === "it" ? "Caricamento..." : "Loading..."}
      </div>
    );
  }

  if (connectStatus?.connected && connectStatus?.charges_enabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
          <CheckCircle className="w-6 h-6 text-emerald-500" />
          <div className="flex-1">
            <p className="font-medium text-emerald-700 dark:text-emerald-300">
              {language === "it" ? "Account Stripe Collegato" : "Stripe Account Connected"}
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              {language === "it" 
                ? "I pagamenti dei clienti vanno direttamente al tuo account."
                : "Client payments go directly to your account."
              }
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={openDashboard} className="flex-1">
            <ExternalLink className="w-4 h-4 mr-2" />
            {language === "it" ? "Dashboard Stripe" : "Stripe Dashboard"}
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === "it" ? "Disconnetti" : "Disconnect")}
          </Button>
        </div>
      </div>
    );
  }

  if (connectStatus?.connected && !connectStatus?.charges_enabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
          <AlertCircle className="w-6 h-6 text-amber-500" />
          <div className="flex-1">
            <p className="font-medium text-amber-700 dark:text-amber-300">
              {language === "it" ? "Configurazione incompleta" : "Setup incomplete"}
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {language === "it" 
                ? "Completa la configurazione del tuo account Stripe per ricevere pagamenti."
                : "Complete your Stripe account setup to receive payments."
              }
            </p>
          </div>
        </div>
        
        <Button onClick={handleConnect} disabled={connecting} className="w-full">
          {connecting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Link className="w-4 h-4 mr-2" />
          )}
          {language === "it" ? "Completa Configurazione" : "Complete Setup"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
        <p className="text-sm text-muted-foreground">
          {language === "it" 
            ? "Nessun account Stripe collegato. I pagamenti dei clienti non potranno essere ricevuti direttamente."
            : "No Stripe account connected. Client payments cannot be received directly."
          }
        </p>
      </div>
      
      <Button onClick={handleConnect} disabled={connecting} className="w-full bg-violet-500 hover:bg-violet-600">
        {connecting ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Link className="w-4 h-4 mr-2" />
        )}
        {language === "it" ? "Collega Account Stripe" : "Connect Stripe Account"}
      </Button>
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [bookingUrl, setBookingUrl] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    logo_url: "",
    cover_image: "",
    max_concurrent_bookings: 1,
    branding: {
      primary_color: "#6366f1",
      secondary_color: "#8b5cf6",
      accent_color: "#ec4899"
    },
    social_links: {
      instagram: "",
      facebook: "",
      whatsapp: "",
      website: ""
    },
    settings: {
      opening_hours: {},
      slot_interval: 15,
      slot_buffer_minutes: 10,
      booking_advance_days: 30,
      reminders_enabled: true,
      closure_periods: []
    }
  });

  // State for adding new closure period
  const [newClosure, setNewClosure] = useState({
    start_date: null,
    end_date: null,
    reason: ""
  });
  const [showClosureForm, setShowClosureForm] = useState(false);

  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    payments_enabled: false,
    require_payment: false,
    allow_deposit: true,
    deposit_percentage: 30,
    minimum_deposit: 5.0
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Multi-sede locations state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "",
    slug: "",
    address: "",
    phone: "",
    logo_url: "",
    branding: {
      primary_color: "#6366f1",
      secondary_color: "#8b5cf6",
      accent_color: "#ec4899"
    }
  });
  const [editLocationServices, setEditLocationServices] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    duration: 30,
    price: 0,
    category: "Generale"
  });

  const days = [
    { key: "monday", label: language === "it" ? "Lunedì" : "Monday" },
    { key: "tuesday", label: language === "it" ? "Martedì" : "Tuesday" },
    { key: "wednesday", label: language === "it" ? "Mercoledì" : "Wednesday" },
    { key: "thursday", label: language === "it" ? "Giovedì" : "Thursday" },
    { key: "friday", label: language === "it" ? "Venerdì" : "Friday" },
    { key: "saturday", label: language === "it" ? "Sabato" : "Saturday" },
    { key: "sunday", label: language === "it" ? "Domenica" : "Sunday" }
  ];

  useEffect(() => {
    if (user?.tenant_id) {
      fetchTenant();
      fetchQrCode();
      fetchPaymentSettings();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchTenant = async () => {
    try {
      const response = await axios.get(`${API}/tenants/${user.tenant_id}`, {
        withCredentials: true
      });
      setTenant(response.data);
      const existingSettings = response.data.settings || {};
      const existingBranding = response.data.branding || {};
      const existingSocialLinks = response.data.social_links || {};
      setFormData({
        name: response.data.name,
        phone: response.data.phone || "",
        address: response.data.address || "",
        logo_url: response.data.logo_url || "",
        cover_image: response.data.cover_image || "",
        max_concurrent_bookings: response.data.max_concurrent_bookings || 1,
        branding: {
          primary_color: existingBranding.primary_color || "#6366f1",
          secondary_color: existingBranding.secondary_color || "#8b5cf6",
          accent_color: existingBranding.accent_color || "#ec4899"
        },
        social_links: {
          instagram: existingSocialLinks.instagram || "",
          facebook: existingSocialLinks.facebook || "",
          whatsapp: existingSocialLinks.whatsapp || "",
          website: existingSocialLinks.website || ""
        },
        settings: {
          opening_hours: existingSettings.opening_hours || {},
          slot_interval: existingSettings.slot_interval || 15,
          slot_buffer_minutes:
            existingSettings.slot_buffer_minutes !== undefined
              ? existingSettings.slot_buffer_minutes
              : 10,
          booking_advance_days: existingSettings.booking_advance_days || 30,
          reminders_enabled: existingSettings.reminders_enabled ?? true,
          confirmation_required: existingSettings.confirmation_required ?? true,
          confirmation_hours_before: existingSettings.confirmation_hours_before || 24,
          confirmation_deadline_hours: existingSettings.confirmation_deadline_hours || 6,
          auto_release_unconfirmed: existingSettings.auto_release_unconfirmed ?? false,
          closure_periods: existingSettings.closure_periods || []
        }
      });
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const fetchQrCode = async () => {
    try {
      const tenantResponse = await axios.get(`${API}/tenants/${user.tenant_id}`, {
        withCredentials: true
      });
      const slug = tenantResponse.data.slug;
      
      const response = await axios.get(`${API}/public/salon/${slug}/qrcode`, {
        withCredentials: true
      });
      setQrCode(response.data.qr_code);
      setBookingUrl(response.data.booking_url);
    } catch (error) {
      console.error("Error fetching QR code:", error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payments/salon/settings`, {
        withCredentials: true
      });
      setPaymentSettings({
        payments_enabled: response.data.payments_enabled || false,
        require_payment: response.data.require_payment || false,
        allow_deposit: response.data.allow_deposit !== false,
        deposit_percentage: response.data.deposit_percentage || 30,
        minimum_deposit: response.data.minimum_deposit || 5.0
      });
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    }
  };

  // ============ MULTI-SEDE LOCATION FUNCTIONS ============

  const fetchLocations = async () => {
    if (tenant?.plan_type !== "multi_sede") return;
    
    setLoadingLocations(true);
    try {
      const response = await axios.get(`${API}/tenants/${user.tenant_id}/locations`, {
        withCredentials: true
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch locations when tenant is loaded and is multi_sede
  useEffect(() => {
    if (tenant?.plan_type === "multi_sede") {
      fetchLocations();
    }
  }, [tenant]);

  const handleAddLocation = async () => {
    if (!newLocation.name || !newLocation.slug) {
      toast.error(language === "it" ? "Nome e slug sono obbligatori" : "Name and slug are required");
      return;
    }

    try {
      await axios.post(`${API}/tenants/${user.tenant_id}/locations`, newLocation, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Sede aggiunta!" : "Location added!");
      setNewLocation({
        name: "",
        slug: "",
        address: "",
        phone: "",
        logo_url: "",
        branding: {
          primary_color: "#6366f1",
          secondary_color: "#8b5cf6",
          accent_color: "#ec4899"
        }
      });
      setShowAddLocation(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleUpdateLocation = async (locationId, data) => {
    try {
      await axios.put(`${API}/tenants/${user.tenant_id}/locations/${locationId}`, data, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Sede aggiornata!" : "Location updated!");
      setEditingLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm(language === "it" ? "Sei sicuro di voler eliminare questa sede?" : "Are you sure you want to delete this location?")) {
      return;
    }

    try {
      await axios.delete(`${API}/tenants/${user.tenant_id}/locations/${locationId}`, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Sede eliminata!" : "Location deleted!");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleAddLocationService = async (locationId) => {
    if (!newService.name || newService.price < 0) {
      toast.error(language === "it" ? "Compila tutti i campi" : "Fill all fields");
      return;
    }

    try {
      await axios.post(`${API}/tenants/${user.tenant_id}/locations/${locationId}/services`, newService, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Servizio aggiunto!" : "Service added!");
      setNewService({ name: "", duration: 30, price: 0, category: "Generale" });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const handleDeleteLocationService = async (locationId, serviceId) => {
    try {
      await axios.delete(`${API}/tenants/${user.tenant_id}/locations/${locationId}/services/${serviceId}`, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Servizio eliminato!" : "Service deleted!");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const savePaymentSettings = async () => {
    setSavingPayment(true);
    try {
      await axios.patch(`${API}/payments/salon/settings`, paymentSettings, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Impostazioni pagamento salvate" : "Payment settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/tenants/${user.tenant_id}`, formData, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Impostazioni salvate" : "Settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    } finally {
      setSaving(false);
    }
  };

  const updateOpeningHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        opening_hours: {
          ...prev.settings.opening_hours,
          [day]: {
            ...prev.settings.opening_hours[day],
            [field]: value
          }
        }
      }
    }));
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success(language === "it" ? "URL copiato!" : "URL copied!");
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!user?.tenant_id) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {language === "it" ? "Nessuna attività associata" : "No business associated"}
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-4xl">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t("settings")}</h1>
          <p className="text-muted-foreground mt-1">
            {language === "it" ? "Configura la tua attività" : "Configure your business"}
          </p>
        </div>

        {/* Basic Info */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold">{language === "it" ? "Informazioni Base" : "Basic Info"}</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Nome Salone" : "Salon Name"}
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="setting-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("phone")}</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="setting-phone-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1.5">
              {language === "it" ? "Indirizzo" : "Address"}
            </label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              data-testid="setting-address-input"
            />
          </div>

          {/* Concurrent Bookings / Stations */}
          <div className="p-4 rounded-lg border bg-muted/30">
            <label className="block text-sm font-medium mb-1.5">
              {language === "it" ? "Numero Postazioni / Poltrone" : "Number of Stations / Chairs"}
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              {language === "it" 
                ? "Quanti appuntamenti possono essere presi contemporaneamente nello stesso slot orario" 
                : "How many appointments can be booked at the same time slot"}
            </p>
            <Input
              type="number"
              min="1"
              max="50"
              value={formData.max_concurrent_bookings}
              onChange={(e) => setFormData({ ...formData, max_concurrent_bookings: parseInt(e.target.value) || 1 })}
              className="w-24"
              data-testid="setting-stations-input"
            />
          </div>
        </div>

        {/* White Label / Branding */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Palette className="w-5 h-5" />
            {language === "it" ? "White Label / Branding" : "White Label / Branding"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Personalizza l'aspetto della tua pagina di prenotazione" 
              : "Customize the look of your booking page"}
          </p>
          
          <LogoUploader
            value={formData.logo_url}
            onChange={(url) => setFormData({ ...formData, logo_url: url })}
            language={language}
          />

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Colore Primario" : "Primary Color"}
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.branding.primary_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, primary_color: e.target.value }
                  })}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <Input
                  value={formData.branding.primary_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, primary_color: e.target.value }
                  })}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Colore Secondario" : "Secondary Color"}
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.branding.secondary_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, secondary_color: e.target.value }
                  })}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <Input
                  value={formData.branding.secondary_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, secondary_color: e.target.value }
                  })}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Colore Accento" : "Accent Color"}
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.branding.accent_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, accent_color: e.target.value }
                  })}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <Input
                  value={formData.branding.accent_color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    branding: { ...formData.branding, accent_color: e.target.value }
                  })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 rounded-lg border bg-muted/30">
            <p className="text-xs text-muted-foreground mb-3">
              {language === "it" ? "Anteprima:" : "Preview:"}
            </p>
            <div className="flex items-center gap-3 mb-3">
              {formData.logo_url ? (
                <img 
                  src={formData.logo_url} 
                  alt="Logo preview" 
                  className="h-10 w-auto max-w-[120px] object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: formData.branding.primary_color }}
                >
                  {formData.name?.charAt(0) || "S"}
                </div>
              )}
              <span className="font-semibold">{formData.name || "Nome Salone"}</span>
            </div>
            <div className="flex gap-2">
              <div 
                className="px-4 py-2 rounded text-white text-sm"
                style={{ backgroundColor: formData.branding.primary_color }}
              >
                {language === "it" ? "Pulsante Primario" : "Primary"}
              </div>
              <div 
                className="px-4 py-2 rounded text-white text-sm"
                style={{ backgroundColor: formData.branding.secondary_color }}
              >
                {language === "it" ? "Secondario" : "Secondary"}
              </div>
              <div 
                className="px-4 py-2 rounded text-white text-sm"
                style={{ backgroundColor: formData.branding.accent_color }}
              >
                {language === "it" ? "Accento" : "Accent"}
              </div>
            </div>
          </div>
        </div>

        {/* Multi-Sede Location Management - Only for multi_sede plans */}
        {tenant?.plan_type === "multi_sede" && (
          <div className="bg-card rounded-2xl border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-500" />
                {language === "it" ? "Gestione Sedi" : "Location Management"}
              </h2>
              <span className="text-sm text-muted-foreground">
                {locations.length}/{tenant?.max_locations || 10} {language === "it" ? "sedi" : "locations"}
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {language === "it" 
                ? "Gestisci tutte le tue sedi con white-label, servizi e prezzi personalizzati per ciascuna." 
                : "Manage all your locations with custom white-label, services and prices for each."}
            </p>

            {/* Locations List */}
            {loadingLocations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-8 bg-muted/30 rounded-xl">
                <Store className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  {language === "it" ? "Nessuna sede aggiunta" : "No locations added"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {language === "it" ? "Aggiungi la tua prima sede per iniziare" : "Add your first location to get started"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {locations.map((location) => (
                  <div 
                    key={location.location_id} 
                    className="p-4 rounded-xl border bg-muted/20 hover:bg-muted/30 transition-colors"
                  >
                    {editingLocation === location.location_id ? (
                      /* Edit Mode */
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              {language === "it" ? "Nome Sede" : "Location Name"}
                            </label>
                            <Input
                              value={location.name}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, name: e.target.value } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              {language === "it" ? "Indirizzo" : "Address"}
                            </label>
                            <Input
                              value={location.address || ""}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, address: e.target.value } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              {language === "it" ? "Telefono" : "Phone"}
                            </label>
                            <Input
                              value={location.phone || ""}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, phone: e.target.value } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">URL Logo</label>
                            <Input
                              value={location.logo_url || ""}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, logo_url: e.target.value } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              placeholder="https://..."
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            {language === "it" ? "Colori Brand" : "Brand Colors"}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={location.branding?.primary_color || "#6366f1"}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, branding: { ...l.branding, primary_color: e.target.value } } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              className="w-8 h-8 rounded cursor-pointer border"
                              title="Primary"
                            />
                            <input
                              type="color"
                              value={location.branding?.secondary_color || "#8b5cf6"}
                              onChange={(e) => {
                                const updated = locations.map(l => 
                                  l.location_id === location.location_id 
                                    ? { ...l, branding: { ...l.branding, secondary_color: e.target.value } } 
                                    : l
                                );
                                setLocations(updated);
                              }}
                              className="w-8 h-8 rounded cursor-pointer border"
                              title="Secondary"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateLocation(location.location_id, location)}
                          >
                            <Save className="w-4 h-4 mr-1" />
                            {language === "it" ? "Salva" : "Save"}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingLocation(null);
                              fetchLocations();
                            }}
                          >
                            {language === "it" ? "Annulla" : "Cancel"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {location.logo_url ? (
                              <img src={location.logo_url} alt={location.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: location.branding?.primary_color || "#6366f1" }}
                              >
                                {location.name?.charAt(0) || "S"}
                              </div>
                            )}
                            <div>
                              <h4 className="font-medium">{location.name}</h4>
                              <p className="text-xs text-muted-foreground">/book/{location.slug}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setEditingLocation(location.location_id)}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setEditLocationServices(
                                editLocationServices === location.location_id ? null : location.location_id
                              )}
                            >
                              <Briefcase className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteLocation(location.location_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {location.address && (
                          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {location.address}
                          </p>
                        )}

                        {/* Services Section (Collapsible) */}
                        {editLocationServices === location.location_id && (
                          <div className="mt-4 pt-4 border-t space-y-3">
                            <h5 className="text-sm font-medium flex items-center gap-2">
                              <Scissors className="w-4 h-4 text-violet-500" />
                              {language === "it" ? "Servizi e Prezzi" : "Services & Prices"}
                            </h5>
                            
                            {/* Existing Services */}
                            {(location.services || []).length > 0 ? (
                              <div className="space-y-2">
                                {location.services.map((svc) => (
                                  <div key={svc.service_id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded-lg border">
                                    <div>
                                      <span className="font-medium text-sm">{svc.name}</span>
                                      <span className="text-xs text-muted-foreground ml-2">({svc.duration} min)</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-sm">€{svc.price}</span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-red-500"
                                        onClick={() => handleDeleteLocationService(location.location_id, svc.service_id)}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground py-2">
                                {language === "it" ? "Nessun servizio personalizzato" : "No custom services"}
                              </p>
                            )}
                            
                            {/* Add New Service */}
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Input
                                  placeholder={language === "it" ? "Nome servizio" : "Service name"}
                                  value={newService.name}
                                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                                  className="text-sm"
                                />
                              </div>
                              <div className="w-20">
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  value={newService.duration}
                                  onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) || 30 })}
                                  className="text-sm"
                                />
                              </div>
                              <div className="w-24">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                                  <Input
                                    type="number"
                                    placeholder="0"
                                    value={newService.price}
                                    onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })}
                                    className="text-sm pl-6"
                                  />
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAddLocationService(location.location_id)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add New Location */}
            {locations.length < (tenant?.max_locations || 10) && (
              <div>
                {showAddLocation ? (
                  <div className="p-4 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/50 dark:bg-violet-900/10 space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Plus className="w-4 h-4 text-violet-500" />
                      {language === "it" ? "Nuova Sede" : "New Location"}
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">
                          {language === "it" ? "Nome Sede *" : "Location Name *"}
                        </label>
                        <Input
                          value={newLocation.name}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            name: e.target.value,
                            slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-")
                          })}
                          placeholder={language === "it" ? "Es: Sede Centro" : "Ex: Downtown Branch"}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Slug (URL) *</label>
                        <Input
                          value={newLocation.slug}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                          })}
                          placeholder="sede-centro"
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">{language === "it" ? "Indirizzo" : "Address"}</label>
                        <Input
                          value={newLocation.address}
                          onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">{language === "it" ? "Telefono" : "Phone"}</label>
                        <Input
                          value={newLocation.phone}
                          onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">{language === "it" ? "Colori Brand" : "Brand Colors"}</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={newLocation.branding.primary_color}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            branding: { ...newLocation.branding, primary_color: e.target.value }
                          })}
                          className="w-8 h-8 rounded cursor-pointer border"
                          title="Primary"
                        />
                        <input
                          type="color"
                          value={newLocation.branding.secondary_color}
                          onChange={(e) => setNewLocation({ 
                            ...newLocation, 
                            branding: { ...newLocation.branding, secondary_color: e.target.value }
                          })}
                          className="w-8 h-8 rounded cursor-pointer border"
                          title="Secondary"
                        />
                        <Input
                          value={newLocation.logo_url}
                          onChange={(e) => setNewLocation({ ...newLocation, logo_url: e.target.value })}
                          placeholder={language === "it" ? "URL Logo (opzionale)" : "Logo URL (optional)"}
                          className="text-sm flex-1"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleAddLocation}>
                        <Plus className="w-4 h-4 mr-1" />
                        {language === "it" ? "Aggiungi Sede" : "Add Location"}
                      </Button>
                      <Button variant="outline" onClick={() => setShowAddLocation(false)}>
                        {language === "it" ? "Annulla" : "Cancel"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full border-dashed border-2"
                    onClick={() => setShowAddLocation(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {language === "it" ? "Aggiungi Sede" : "Add Location"}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cover Image & Social Links */}
        <div className="bg-card rounded-2xl border p-6 space-y-6">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Image className="w-5 h-5" />
            {language === "it" ? "Immagine di Copertina & Social" : "Cover Image & Social"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Aggiungi una foto della tua attività e i link ai social per la pagina prenotazioni" 
              : "Add a photo of your business and social links for the booking page"}
          </p>
          
          {/* Cover Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-3">
              {language === "it" ? "Foto di Copertina" : "Cover Photo"}
            </label>
            <CoverImageUploader
              value={formData.cover_image}
              onChange={(url) => setFormData({ ...formData, cover_image: url })}
              language={language}
            />
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              {language === "it" ? "Link Social Media" : "Social Media Links"}
            </h3>
            <p className="text-xs text-muted-foreground">
              {language === "it" 
                ? "I clienti potranno contattarti anche dai tuoi canali social" 
                : "Clients will be able to contact you from your social channels"}
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                    <Instagram className="w-3.5 h-3.5 text-white" />
                  </div>
                  Instagram
                </label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, instagram: e.target.value }
                  })}
                  placeholder="https://instagram.com/tuaattivita"
                  data-testid="setting-instagram-input"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                    <Facebook className="w-3.5 h-3.5 text-white" />
                  </div>
                  Facebook
                </label>
                <Input
                  value={formData.social_links.facebook}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, facebook: e.target.value }
                  })}
                  placeholder="https://facebook.com/tuaattivita"
                  data-testid="setting-facebook-input"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <MessageCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                  WhatsApp
                </label>
                <Input
                  value={formData.social_links.whatsapp}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, whatsapp: e.target.value }
                  })}
                  placeholder="+39 320 1234567"
                  data-testid="setting-whatsapp-input"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-1.5">
                  <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center">
                    <Globe className="w-3.5 h-3.5 text-white" />
                  </div>
                  {language === "it" ? "Sito Web" : "Website"}
                </label>
                <Input
                  value={formData.social_links.website}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_links: { ...formData.social_links, website: e.target.value }
                  })}
                  placeholder="https://www.tuaattivita.it"
                  data-testid="setting-website-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Opening Hours */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {language === "it" ? "Orari di Apertura" : "Opening Hours"}
          </h2>
          
          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Configura gli orari per ogni giorno. Puoi scegliere orario continuato o turni mattina/pomeriggio." 
              : "Configure hours for each day. You can choose continuous hours or morning/afternoon shifts."}
          </p>
          
          <div className="space-y-4">
            {days.map((day) => {
              const hours = formData.settings.opening_hours[day.key] || { 
                closed: false,
                continuous: true,
                start: "09:00", 
                end: "19:00",
                morning_start: "09:00",
                morning_end: "13:00",
                afternoon_start: "15:00",
                afternoon_end: "19:00"
              };
              
              return (
                <div key={day.key} className="p-4 rounded-xl bg-muted/30 border">
                  {/* Day header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-base w-24">{day.label}</span>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!hours.closed}
                          onCheckedChange={(checked) => updateOpeningHours(day.key, "closed", !checked)}
                        />
                        <span className={`text-sm ${hours.closed ? "text-red-500" : "text-green-600"}`}>
                          {hours.closed ? (language === "it" ? "Chiuso" : "Closed") : (language === "it" ? "Aperto" : "Open")}
                        </span>
                      </div>
                    </div>
                    
                    {/* Continuous/Split toggle */}
                    {!hours.closed && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateOpeningHours(day.key, "continuous", true)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                            hours.continuous !== false
                              ? "bg-violet-500 text-white"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {language === "it" ? "Continuato" : "Continuous"}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateOpeningHours(day.key, "continuous", false)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                            hours.continuous === false
                              ? "bg-violet-500 text-white"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {language === "it" ? "Mattina/Pomeriggio" : "Morning/Afternoon"}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Time inputs */}
                  {!hours.closed && (
                    <div className="mt-3">
                      {hours.continuous !== false ? (
                        /* Continuous hours */
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm text-muted-foreground w-16">
                            {language === "it" ? "Orario:" : "Hours:"}
                          </span>
                          <Input
                            type="time"
                            value={hours.start || "09:00"}
                            onChange={(e) => updateOpeningHours(day.key, "start", e.target.value)}
                            className="w-28"
                          />
                          <span className="text-muted-foreground">—</span>
                          <Input
                            type="time"
                            value={hours.end || "19:00"}
                            onChange={(e) => updateOpeningHours(day.key, "end", e.target.value)}
                            className="w-28"
                          />
                        </div>
                      ) : (
                        /* Split hours - Morning and Afternoon */
                        <div className="space-y-3">
                          {/* Morning */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-medium text-amber-600 dark:text-amber-400 w-16 flex items-center gap-1">
                              <span className="text-lg">☀️</span>
                              {language === "it" ? "Matt." : "AM"}
                            </span>
                            <Input
                              type="time"
                              value={hours.morning_start || "09:00"}
                              onChange={(e) => updateOpeningHours(day.key, "morning_start", e.target.value)}
                              className="w-28"
                            />
                            <span className="text-muted-foreground">—</span>
                            <Input
                              type="time"
                              value={hours.morning_end || "13:00"}
                              onChange={(e) => updateOpeningHours(day.key, "morning_end", e.target.value)}
                              className="w-28"
                            />
                          </div>
                          
                          {/* Afternoon */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-medium text-orange-600 dark:text-orange-400 w-16 flex items-center gap-1">
                              <span className="text-lg">🌙</span>
                              {language === "it" ? "Pom." : "PM"}
                            </span>
                            <Input
                              type="time"
                              value={hours.afternoon_start || "15:00"}
                              onChange={(e) => updateOpeningHours(day.key, "afternoon_start", e.target.value)}
                              className="w-28"
                            />
                            <span className="text-muted-foreground">—</span>
                            <Input
                              type="time"
                              value={hours.afternoon_end || "19:00"}
                              onChange={(e) => updateOpeningHours(day.key, "afternoon_end", e.target.value)}
                              className="w-28"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Booking Settings */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold">{language === "it" ? "Impostazioni Prenotazione" : "Booking Settings"}</h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Intervallo Slot (minuti)" : "Slot Interval (minutes)"}
              </label>
              <Input
                type="number"
                min="5"
                max="60"
                step="5"
                value={formData.settings.slot_interval}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, slot_interval: parseInt(e.target.value) }
                })}
                data-testid="settings-slot-interval"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {language === "it"
                  ? "Ogni quanti minuti viene proposto un nuovo slot."
                  : "How often a new slot is offered."}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Pausa tra appuntamenti (min)" : "Buffer between appointments (min)"}
              </label>
              <Input
                type="number"
                min="0"
                max="60"
                step="5"
                value={formData.settings.slot_buffer_minutes ?? 0}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, slot_buffer_minutes: parseInt(e.target.value) || 0 }
                })}
                data-testid="settings-slot-buffer"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {language === "it"
                  ? "Minuti di pausa minima tra un appuntamento e il successivo (riordino sala)."
                  : "Min downtime between back-to-back appointments (room reset)."}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Prenotazione max giorni in anticipo" : "Max advance booking days"}
              </label>
              <Input
                type="number"
                min="1"
                max="365"
                value={formData.settings.booking_advance_days}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, booking_advance_days: parseInt(e.target.value) }
                })}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={formData.settings.reminders_enabled}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                settings: { ...formData.settings, reminders_enabled: checked }
              })}
            />
            <label className="text-sm">
              {language === "it" ? "Abilita reminder automatici (24h, 12h, 2h)" : "Enable automatic reminders (24h, 12h, 2h)"}
            </label>
          </div>
        </div>

        {/* Closure Periods / Holidays */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-red-500" />
            {language === "it" ? "Chiusure Straordinarie" : "Closure Periods"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Aggiungi periodi di chiusura per ferie, festività o altri motivi. In questi giorni i clienti non potranno prenotare." 
              : "Add closure periods for holidays or other reasons. Clients won't be able to book during these days."}
          </p>

          {/* Existing closure periods */}
          {formData.settings.closure_periods && formData.settings.closure_periods.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {language === "it" ? "Periodi di chiusura programmati:" : "Scheduled closure periods:"}
              </h3>
              <div className="space-y-2">
                {formData.settings.closure_periods.map((period, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                  >
                    <div className="flex items-center gap-3">
                      <Plane className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-red-700 dark:text-red-300">
                          {format(parseISO(period.start_date), "d MMM yyyy", { locale: language === "it" ? it : enUS })}
                          {period.start_date !== period.end_date && (
                            <> — {format(parseISO(period.end_date), "d MMM yyyy", { locale: language === "it" ? it : enUS })}</>
                          )}
                        </p>
                        {period.reason && (
                          <p className="text-sm text-red-600 dark:text-red-400">{period.reason}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updatedPeriods = formData.settings.closure_periods.filter((_, i) => i !== index);
                        setFormData({
                          ...formData,
                          settings: { ...formData.settings, closure_periods: updatedPeriods }
                        });
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30"
                      data-testid={`delete-closure-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new closure period */}
          {!showClosureForm ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowClosureForm(true)}
              className="w-full border-dashed border-2 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid="add-closure-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {language === "it" ? "Aggiungi Periodo di Chiusura" : "Add Closure Period"}
            </Button>
          ) : (
            <div className="p-4 rounded-xl border-2 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <CalendarX className="w-4 h-4 text-red-500" />
                {language === "it" ? "Nuovo Periodo di Chiusura" : "New Closure Period"}
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Data Inizio" : "Start Date"}
                  </label>
                  <Input
                    type="date"
                    value={newClosure.start_date || ""}
                    onChange={(e) => setNewClosure({ ...newClosure, start_date: e.target.value })}
                    min={format(new Date(), "yyyy-MM-dd")}
                    className="w-full"
                    data-testid="closure-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {language === "it" ? "Data Fine" : "End Date"}
                  </label>
                  <Input
                    type="date"
                    value={newClosure.end_date || ""}
                    onChange={(e) => setNewClosure({ ...newClosure, end_date: e.target.value })}
                    min={newClosure.start_date || format(new Date(), "yyyy-MM-dd")}
                    className="w-full"
                    data-testid="closure-end-date"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Motivo (opzionale)" : "Reason (optional)"}
                </label>
                <Input
                  value={newClosure.reason}
                  onChange={(e) => setNewClosure({ ...newClosure, reason: e.target.value })}
                  placeholder={language === "it" ? "es. Ferie estive, Ristrutturazione..." : "e.g. Summer holidays, Renovation..."}
                  data-testid="closure-reason"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowClosureForm(false);
                    setNewClosure({ start_date: null, end_date: null, reason: "" });
                  }}
                >
                  {language === "it" ? "Annulla" : "Cancel"}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (!newClosure.start_date || !newClosure.end_date) {
                      toast.error(language === "it" ? "Seleziona le date" : "Select dates");
                      return;
                    }
                    const updatedPeriods = [
                      ...(formData.settings.closure_periods || []),
                      {
                        start_date: newClosure.start_date,
                        end_date: newClosure.end_date,
                        reason: newClosure.reason
                      }
                    ];
                    setFormData({
                      ...formData,
                      settings: { ...formData.settings, closure_periods: updatedPeriods }
                    });
                    setShowClosureForm(false);
                    setNewClosure({ start_date: null, end_date: null, reason: "" });
                    toast.success(language === "it" ? "Periodo di chiusura aggiunto" : "Closure period added");
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-closure-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === "it" ? "Aggiungi" : "Add"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Anti No-Show Settings */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold">
            {language === "it" ? "Sistema Anti No-Show" : "Anti No-Show System"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Richiedi conferma ai clienti prima dell'appuntamento per ridurre i no-show"
              : "Request confirmation from clients before appointment to reduce no-shows"
            }
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.settings.confirmation_required ?? true}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, confirmation_required: checked }
                })}
              />
              <label className="text-sm">
                {language === "it" ? "Richiedi conferma appuntamento" : "Require appointment confirmation"}
              </label>
            </div>

            {formData.settings.confirmation_required && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {language === "it" ? "Invia richiesta conferma (ore prima)" : "Send confirmation request (hours before)"}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="72"
                      value={formData.settings.confirmation_hours_before ?? 24}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, confirmation_hours_before: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {language === "it" ? "Scadenza conferma (ore)" : "Confirmation deadline (hours)"}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      value={formData.settings.confirmation_deadline_hours ?? 6}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, confirmation_deadline_hours: parseInt(e.target.value) }
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === "it" 
                        ? "Il cliente ha questo tempo per confermare dopo la richiesta"
                        : "Client has this time to confirm after request"
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <Switch
                    checked={formData.settings.auto_release_unconfirmed ?? false}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, auto_release_unconfirmed: checked }
                    })}
                  />
                  <div>
                    <label className="text-sm font-medium">
                      {language === "it" ? "Libera slot automaticamente" : "Auto-release slot"}
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {language === "it" 
                        ? "Se il cliente non conferma, lo slot viene cancellato e reso disponibile"
                        : "If client doesn't confirm, slot is cancelled and made available"
                      }
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* QR Code & Booking URL */}
        <div className="bg-card rounded-2xl border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            {language === "it" ? "Pagina Prenotazione" : "Booking Page"}
          </h2>
          
          <div className="flex flex-col md:flex-row gap-6">
            {qrCode && (
              <div className="flex-shrink-0">
                <img src={qrCode} alt="QR Code" className="w-48 h-48 rounded-xl" />
              </div>
            )}
            
            <div className="flex-1 space-y-4">
              <p className="text-muted-foreground">
                {language === "it" 
                  ? "Condividi questo link o QR code con i tuoi clienti per permettere prenotazioni rapide."
                  : "Share this link or QR code with your clients for quick bookings."
                }
              </p>
              
              <div className="flex gap-2">
                <Input value={bookingUrl} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={copyUrl}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-card rounded-2xl border p-6 space-y-4" data-testid="admin-notification-settings">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            {language === "it" ? "Notifiche Push" : "Push Notifications"}
          </h2>
          
          <p className="text-muted-foreground">
            {language === "it" 
              ? "Ricevi notifiche in tempo reale quando ricevi nuove prenotazioni, conferme o cancellazioni."
              : "Receive real-time notifications when you get new bookings, confirmations or cancellations."
            }
          </p>
          
          <NotificationToggle language={language} />
        </div>

        {/* Payment Settings */}
        <div className="bg-card rounded-2xl border p-6 space-y-6" data-testid="payment-settings">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {language === "it" ? "Pagamenti Online" : "Online Payments"}
          </h2>
          
          <p className="text-muted-foreground">
            {language === "it" 
              ? "Permetti ai clienti di pagare online al momento della prenotazione per ridurre le cancellazioni."
              : "Allow clients to pay online when booking to reduce cancellations."
            }
          </p>
          
          {/* Enable Payments */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium">{language === "it" ? "Abilita pagamenti" : "Enable payments"}</p>
              <p className="text-sm text-muted-foreground">
                {language === "it" 
                  ? "Mostra le opzioni di pagamento nella pagina di prenotazione"
                  : "Show payment options on the booking page"}
              </p>
            </div>
            <Switch
              checked={paymentSettings.payments_enabled}
              onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, payments_enabled: checked }))}
              data-testid="payment-enabled-switch"
            />
          </div>

          {/* Require Payment */}
          <div className={`flex items-center justify-between py-3 border-b ${!paymentSettings.payments_enabled ? 'opacity-50' : ''}`}>
            <div>
              <p className="font-medium">{language === "it" ? "Pagamento obbligatorio" : "Require payment"}</p>
              <p className="text-sm text-muted-foreground">
                {language === "it" 
                  ? "I clienti devono pagare per confermare la prenotazione"
                  : "Clients must pay to confirm their booking"}
              </p>
            </div>
            <Switch
              checked={paymentSettings.require_payment}
              onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, require_payment: checked }))}
              disabled={!paymentSettings.payments_enabled}
              data-testid="payment-required-switch"
            />
          </div>

          {/* Allow Deposit */}
          <div className={`flex items-center justify-between py-3 border-b ${!paymentSettings.payments_enabled ? 'opacity-50' : ''}`}>
            <div>
              <p className="font-medium">{language === "it" ? "Permetti acconto" : "Allow deposit"}</p>
              <p className="text-sm text-muted-foreground">
                {language === "it" 
                  ? "I clienti possono scegliere di pagare solo un acconto"
                  : "Clients can choose to pay only a deposit"}
              </p>
            </div>
            <Switch
              checked={paymentSettings.allow_deposit}
              onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, allow_deposit: checked }))}
              disabled={!paymentSettings.payments_enabled}
              data-testid="allow-deposit-switch"
            />
          </div>

          {/* Deposit Percentage */}
          <div className={`space-y-2 ${!paymentSettings.payments_enabled || !paymentSettings.allow_deposit ? 'opacity-50' : ''}`}>
            <label className="font-medium flex items-center gap-2">
              <Euro className="w-4 h-4" />
              {language === "it" ? "Percentuale acconto" : "Deposit percentage"}
            </label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min="10"
                max="100"
                value={paymentSettings.deposit_percentage}
                onChange={(e) => setPaymentSettings(prev => ({ ...prev, deposit_percentage: parseInt(e.target.value) || 30 }))}
                className="w-24"
                disabled={!paymentSettings.payments_enabled || !paymentSettings.allow_deposit}
                data-testid="deposit-percentage-input"
              />
              <span className="text-muted-foreground">%</span>
              <span className="text-sm text-muted-foreground">
                ({language === "it" ? "minimo €5" : "minimum €5"})
              </span>
            </div>
          </div>

          {/* Save Payment Settings Button */}
          <Button 
            onClick={savePaymentSettings} 
            disabled={savingPayment}
            variant="outline"
            className="w-full"
            data-testid="save-payment-settings-btn"
          >
            {savingPayment ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                {language === "it" ? "Salvataggio..." : "Saving..."}
              </span>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                {language === "it" ? "Salva impostazioni pagamento" : "Save payment settings"}
              </>
            )}
          </Button>
        </div>

        {/* Stripe Connect Section */}
        <div className="bg-card rounded-2xl border p-6 space-y-6" data-testid="stripe-connect-settings">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-500" />
            {language === "it" ? "Ricevi Pagamenti - Stripe Connect" : "Receive Payments - Stripe Connect"}
          </h2>
          
          <p className="text-muted-foreground">
            {language === "it" 
              ? "Collega il tuo account Stripe per ricevere i pagamenti direttamente sul tuo conto. I pagamenti dei clienti andranno a te al 100%."
              : "Connect your Stripe account to receive payments directly. Client payments will go 100% to you."
            }
          </p>

          <StripeConnectSection language={language} />
        </div>

        {/* WhatsApp Business Section */}
        <div className="bg-card rounded-2xl border p-6 space-y-6" data-testid="whatsapp-settings">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <WhatsAppIcon className="w-5 h-5 text-green-500" />
            {language === "it" ? "Reminder WhatsApp" : "WhatsApp Reminders"}
          </h2>
          
          <p className="text-muted-foreground">
            {language === "it" 
              ? "I reminder WhatsApp vengono configurati e gestiti centralmente da SmartBooker per garantire la massima affidabilità."
              : "WhatsApp reminders are centrally configured and managed by SmartBooker to ensure maximum reliability."
            }
          </p>

          <WhatsAppReminderStatus language={language} />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full btn-lift" data-testid="save-settings-btn">
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                {language === "it" ? "Salvataggio..." : "Saving..."}
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t("save")} {t("settings")}
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;
